import React, { useEffect, useMemo, useState } from 'react';
import type { DailyData } from '../../types';
import type { WidgetSize } from '../../types/dashboard';
import StreakLineAnimation from './StreakLineAnimation';
import { computeTradeLevelStats } from '../../lib/trades/tradeLevelStats';

interface MaxConsecutiveLossesWidgetProps {
  dailyData: DailyData[];
  size?: WidgetSize;
  /** Authoritative trade records; when provided, streaks use the trade sequence. */
  trades?: any[];
}

const VALUE_FONT_BY_SIZE: Record<WidgetSize, string> = {
  mini: 'text-5xl',
  regular: 'text-6xl',
  large: 'text-7xl',
  full: 'text-7xl',
};

export const MaxConsecutiveLossesWidget: React.FC<MaxConsecutiveLossesWidgetProps> = ({ dailyData, size = 'mini', trades }) => {
  const [isDarkMode, setIsDarkMode] = useState(() => {
    if (typeof document === 'undefined') return true;
    return document.documentElement.classList.contains('dark');
  });

  useEffect(() => {
    if (typeof document === 'undefined') return;
    const root = document.documentElement;
    const update = () => setIsDarkMode(root.classList.contains('dark'));
    update();
    const observer = new MutationObserver(update);
    observer.observe(root, { attributes: true, attributeFilter: ['class'] });
    return () => observer.disconnect();
  }, []);

  const maxLosses = useMemo(() => {
    if (Array.isArray(trades) && trades.length > 0) {
      return computeTradeLevelStats(trades).maxConsecutiveLosses;
    }
    let maxStreak = 0;
    let currentStreak = 0;
    for (const day of dailyData) {
      if (day.pnl < 0) {
        currentStreak++;
      } else {
        maxStreak = Math.max(maxStreak, currentStreak);
        currentStreak = 0;
      }
    }
    return Math.max(maxStreak, currentStreak);
  }, [dailyData, trades]);

  const cardStyle = useMemo(() => ({
    backgroundColor: isDarkMode ? '#0b111a' : '#f8f6f4',
    boxShadow: isDarkMode ? '0 20px 35px rgba(0,0,0,0.45)' : '0 25px 35px rgba(15,23,42,0.12)',
  }), [isDarkMode]);

  const overlayStyle = useMemo(() => ({
    background: isDarkMode
      ? 'linear-gradient(180deg, rgba(0,0,0,0) 0%, rgba(185,28,28,0.3) 55%, rgba(0,0,0,0.85) 100%)'
      : 'linear-gradient(180deg, rgba(255,255,255,0) 0%, rgba(248,200,204,0.7) 65%, rgba(255,255,255,0.65) 100%)',
  }), [isDarkMode]);

  const edgeFadeStyle = useMemo(() => ({
    background: isDarkMode
      ? 'linear-gradient(90deg, #0b111a 0%, rgba(11,17,26,0) 18%, rgba(11,17,26,0) 82%, #0b111a 100%)'
      : 'linear-gradient(90deg, #f8f6f4 0%, rgba(248,246,244,0) 18%, rgba(248,246,244,0) 82%, #f8f6f4 100%)',
  }), [isDarkMode]);

  const numberColor = isDarkMode ? '#ff9fa0' : '#dc3f45';
  const textStroke = isDarkMode ? 'rgba(255,255,255,0.12)' : 'rgba(255,255,255,0.4)';
  const textShadow = isDarkMode
    ? '0 18px 35px rgba(255,130,130,0.85), 0 0 25px rgba(255,255,255,0.35)'
    : '0 18px 35px rgba(255,137,137,0.5), 0 0 20px rgba(255,255,255,0.6)';
  const glowBandColor = isDarkMode ? 'rgba(255,120,120,0.38)' : 'rgba(255,175,175,0.65)';
  const headerColor = isDarkMode ? '#fcd6d5' : '#7f1d1d';
  const hairlineColor = isDarkMode ? 'rgba(255,153,153,0.38)' : 'rgba(220,63,69,0.4)';

  return (
    <div className="relative h-full w-full rounded-2xl overflow-hidden" style={cardStyle}>
      <div className="absolute inset-0" style={overlayStyle} />
      <StreakLineAnimation variant="losses" isDarkMode={isDarkMode} />
      <div className="pointer-events-none absolute inset-0" style={edgeFadeStyle} />

      <div className="pointer-events-none absolute inset-x-0 top-0 z-20">
        <div className="px-6 pt-5 pb-2">
          <span
            className="text-[0.68rem] uppercase tracking-normal font-semibold"
            style={{ color: headerColor }}
          >
            LOSING STREAK
          </span>
        </div>
        <div className="mx-6 h-px" style={{ background: hairlineColor }} />
      </div>

      <div className="relative z-10 flex h-full items-center justify-center">
        <div className="relative">
          <div
            className="pointer-events-none absolute -inset-x-16 top-1/2 h-16 -translate-y-1/2 blur-[95px] opacity-90"
            style={{ background: `radial-gradient(circle, ${glowBandColor} 0%, transparent 65%)` }}
          />
          <span
            className={`relative font-extrabold leading-none tabular-nums drop-shadow-[0_15px_30px_rgba(0,0,0,0.45)] ${VALUE_FONT_BY_SIZE[size]}`}
            style={{ color: numberColor, textShadow, WebkitTextStroke: `1px ${textStroke}` }}
          >
            {maxLosses}
          </span>
        </div>
      </div>
    </div>
  );
};
